import { useQuery } from '@tanstack/react-query';
import { fetchHospitalStats } from '@/lib/api/hospital-stats';
import { useAuthStore } from '@/lib/stores/authStore';
import { todayIst, type DateRange } from '@/lib/utils/hospitalStatsCalc';

const LIVE_REFRESH_MS = 30_000;

/**
 * Hospital statistics for an IST date range. Refreshes every 30s only when the range
 * is exactly today, so numbers stay live there. Longer ranges load once (re-running a
 * whole range repeats hundreds of requests); use `refetch` to reload them.
 */
export function useHospitalStats(range: DateRange) {
  const projectId = useAuthStore((state) => state.user?.projectId);
  const today = todayIst();
  const isTodayOnly = range.from === today && range.to === today;

  return useQuery({
    queryKey: ['hospital-stats', projectId, range.from, range.to],
    queryFn: ({ signal }) => fetchHospitalStats(range, signal),
    enabled: !!range.from && !!range.to && range.from <= range.to,
    staleTime: isTodayOnly ? 15_000 : 5 * 60_000,
    refetchInterval: isTodayOnly ? LIVE_REFRESH_MS : false,
  });
}
